/**
 * Class V2 API Client
 * API functions for class detail
 */

import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';
import type { ClassDetail } from '@/types/discovery';

/**
 * Raw API response with double nesting
 * Backend wraps responses in: { data: { data: actualData }, success, timestamp, request_id }
 * The apiClient extracts the first data field, leaving us with { data: RawClassDetail }
 */
interface RawClassDetailResponse {
  data: RawClassDetail;
}

/**
 * Raw class detail from API (snake_case)
 */
interface RawClassDetail {
  id: string;
  name: string;
  level?: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  description?: string;
  bookingUrl?: string | null;
  instructor?: RawInstructorDetail;
  studio?: RawStudioSummary;
}

interface RawInstructorDetail {
  id: string;
  name: string;
  imageUrl?: string;
  genres: string[];
  averageRating?: number;
  reviewCount?: number;
  studios: Array<{
    id: string;
    name: string;
    logoUrl?: string;
    averageRating?: number;
    reviewCount?: number;
  }>;
}

interface RawStudioSummary {
  id: string;
  name: string;
  address?: string;
  logoUrl?: string; // Backend provides camelCase
  averageRating?: number;
  reviewCount?: number;
}

/**
 * Convert snake_case API response to camelCase
 */
function toClassDetail(raw: RawClassDetail): ClassDetail {
  return {
    id: raw.id,
    name: raw.name,
    level: raw.level,
    startTime: raw.startTime,
    endTime: raw.endTime,
    durationMinutes: raw.durationMinutes,
    description: raw.description,
    bookingUrl: raw.bookingUrl ?? undefined,
    instructor: raw.instructor
      ? {
          id: raw.instructor.id,
          name: raw.instructor.name,
          imageUrl: raw.instructor.imageUrl,
          genres: raw.instructor.genres,
          averageRating: raw.instructor.averageRating,
          reviewCount: raw.instructor.reviewCount,
          studios: raw.instructor.studios.map((s) => ({
            id: s.id,
            name: s.name,
            logoUrl: s.logoUrl,
            averageRating: s.averageRating,
            reviewCount: s.reviewCount,
          })),
        }
      : undefined,
    studio: raw.studio
      ? {
          id: raw.studio.id,
          name: raw.studio.name,
          address: raw.studio.address,
          logoUrl: raw.studio.logoUrl,
          averageRating: raw.studio.averageRating,
          reviewCount: raw.studio.reviewCount,
        }
      : undefined,
  };
}

/**
 * Get class detail by ID
 */
export const getClassDetail = async (classId: string): Promise<Result<ClassDetail, ApiError>> => {
  try {
    const response = await apiClient.get<RawClassDetailResponse>(`/v2/classes/${classId}`);

    if (!response.ok) {
      return err(response.error);
    }

    // apiClient extracts the first data field, leaving { data: RawClassDetail }
    // We need to extract the nested data field to get the actual class detail
    const wrappedResponse = response.value;
    const rawClassDetail = wrappedResponse.data;
    const classDetail = toClassDetail(rawClassDetail);

    return ok(classDetail);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch class detail',
    });
  }
};
